import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
} from '@usealloy/component-library';
import type { TabsProps } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';

import { PersonUpdatedOverview } from './PersonUpdatedOverview';
import type { CoreRiskData } from '../../RiskRatingCard/useRiskData';
import { RiskRatingCard } from '../../RiskRatingCard/RiskRatingCard';
import { useEventTypeEvaluationsContext } from '../context';
import { useGetWatchlistMatches } from '../../watchlist-review/hooks/getWatchlistMatches';
import { ReviewTabCount } from '../../watchlist-review/styles';
import { WatchlistMatches } from '../tabs';
import { RiskScoreTabLabel } from '../../RiskRatingCard/RiskScoreTabLabel';
import { TAB_VALUES } from '../tabs/constants';

interface PersonUpdatedTabsProps {
  canViewRiskFormula: boolean;
  riskData: CoreRiskData;
  sessionRiskData: CoreRiskData;
  shouldRenderRiskTab: boolean;
  shouldRenderSessionRiskTab: boolean;
  shouldRenderWatchlistTab: boolean;
}

export const PersonUpdatedTabs = ({
  canViewRiskFormula,
  riskData,
  sessionRiskData,
  shouldRenderRiskTab,
  shouldRenderSessionRiskTab,
  shouldRenderWatchlistTab,
}: PersonUpdatedTabsProps) => {
  const [tab, setTab] = useState(TAB_VALUES.OVERVIEW);

  const { evaluation } = useEventTypeEvaluationsContext();
  const {
    evaluation_token: evaluationToken,
    entity_token: entityToken,
  } = evaluation;
  const { typhonDomain } = useContext(UserContext);

  const [{
    data: { matches = [] },
    isLoading: isLoadingWatchlistMatches,
  }] = useGetWatchlistMatches({
    evaluations: [{
      evaluationToken,
      entityToken,
    }],
    typhonDomain,
    showLatestDecisions: true,
  });
  const {
    riskFactors, riskFormula, riskRangeValues, totalRisk, riskRatingColor, riskRatingLabel,
  } = riskData;

  const tabs = useMemo(() => {
    const reviewMatchesLength = matches
      ? matches
        .filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
        .length
      : 0;

    const allTabs: TabsProps['tabs'] = [
      {
        label: 'Overview',
        value: TAB_VALUES.OVERVIEW,
        content: <PersonUpdatedOverview />,
      },
    ];

    if (shouldRenderWatchlistTab) {
      allTabs.push({
        label: (
          <Box direction="row" gap={1}>
            Watchlist Matches
            {!isLoadingWatchlistMatches && reviewMatchesLength > 0 && (
              <ReviewTabCount status="danger">
                <Box justifyContent="center" alignItems="center">
                  {reviewMatchesLength}
                </Box>
              </ReviewTabCount>
            )}
          </Box>
        ),
        value: TAB_VALUES.WATCHLIST,
        content: <WatchlistMatches evaluation={evaluation} />,
      });
    }

    if (shouldRenderRiskTab) {
      allTabs.push({
        label: (
          <RiskScoreTabLabel
            riskRatingColor={riskRatingColor}
            riskRatingLabel={riskRatingLabel}
          />
        ),
        value: TAB_VALUES.RISK_SCORE,
        content: (
          <RiskRatingCard
            canViewRiskFormula={canViewRiskFormula}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={riskFactors}
            riskOutputFormula={riskFormula}
            riskRanges={riskRangeValues}
            riskValue={totalRisk}
            riskValueColor={riskRatingColor}
            riskValueLabel={riskRatingLabel}
            variant="entityRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }

    if (shouldRenderSessionRiskTab) {
      allTabs.push({
        label: (
          <RiskScoreTabLabel
            riskRatingColor={sessionRiskData.riskRatingColor}
            riskRatingLabel={sessionRiskData.riskRatingLabel}
            variant="session"
          />
        ),
        value: TAB_VALUES.SESSION_RISK_SCORE,
        content: (
          <RiskRatingCard
            canViewRiskFormula={canViewRiskFormula}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={sessionRiskData.riskFactors}
            riskOutputFormula={sessionRiskData.riskFormula}
            riskRanges={sessionRiskData.riskRangeValues}
            riskValue={sessionRiskData.totalRisk}
            riskValueColor={sessionRiskData.riskRatingColor}
            riskValueLabel={sessionRiskData.riskRatingLabel}
            variant="sessionRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }

    return allTabs;
  }, [
    canViewRiskFormula,
    evaluation,
    isLoadingWatchlistMatches,
    matches,
    riskFactors,
    riskFormula,
    riskRangeValues,
    riskRatingColor,
    riskRatingLabel,
    sessionRiskData,
    shouldRenderRiskTab,
    shouldRenderSessionRiskTab,
    shouldRenderWatchlistTab,
    totalRisk,
  ]);

  return (
    <Tabs
      style={{ width: '100%' }}
      tabs={tabs}
      onValueChange={setTab}
      value={tab}
    />
  );
};
